// #2121-spec-kits
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, UncontrolledTooltip, Button } from 'reactstrap';
import { translate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { actionDeleteKit, actionGetKitTypes } from 'store/actions/kitsActions';
import SweetAlert from 'react-bootstrap-sweetalert';
import { actionSweetAlert } from 'store/actions/commonActions';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_TONE = {
  k_st_1:           'secondary',
  k_st_disassembled:         'secondary',
  k_st_2:                'success',
  k_st_reserved:             'info',
  k_st_3: 'info',
  k_st_4:  'primary',
  'k_st_5':      'warning',
  k_st_9:       'warning',
  k_st_6:              'danger',
  k_st_8:                'danger',
  k_st_10:               'warning',
  k_st_11:                   'dark',
  k_st_7:               'secondary',
};

const TableKits = (props) => {
  const { data, page, callback, tabInfo, t, clearKitAction } = props;
  const { is_creating_kits } = useSelector((s) => s.managementUserReducer);
  const kitTypes = useSelector((s) => s.kitsReducer.types);

  const user = useSelector((s) => s.userReducer);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (!Object.keys(kitTypes).length) dispatch(actionGetKitTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editKit        = (e, k) => { e.preventDefault(); history.push(`/admin/kits/form/${k._id}`); };
  const editPatientKit = (e, k) => { e.preventDefault(); history.push(`/admin/kits/patients/details/${k._id}`); };
  const goToOrder      = (e, orderId) => { e.preventDefault(); history.push(`/admin/practice-orders/form/${orderId}`); };

  const copyId = (e, val) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied'));
  };

  const deleteKit = (e, k) => {
    e.preventDefault();
    dispatch(
      actionSweetAlert(
        <SweetAlert
          warning
          title={t('confirm-delete-title') || 'Are you sure you want to delete this kit?'}
          onConfirm={() => {
            dispatch(actionDeleteKit(k._id));
            if (data.length === 1 && page > 0) callback(page - 1);
            dispatch(actionSweetAlert(null));
          }}
          onCancel={() => dispatch(actionSweetAlert(null))}
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="info"
          confirmBtnText={t('confirm-delete-yes') || 'Yes, delete it!'}
          cancelBtnText={t('cancel-btn') || 'Cancel'}
          showCancel
        >
          {t('confirm-delete-warning') || 'All the data related to it will be lost!'}
        </SweetAlert>
      )
    );
  };

  const showPracticeColumn = tabInfo === 'k_st_3' || tabInfo === 'all';

  return (
    <div className="kit-table-wrap">
      <table className="kit-table">
        <thead>
          <tr>
            <th style={{ width: '110px' }}>{t('friendly_id')}</th>
            {showPracticeColumn && <th>{t('practice')}</th>}
            <th style={{ width: '140px' }}>{t('type')}</th>
            <th style={{ width: '210px' }}>{t('status')}</th>
            <th style={{ width: '110px', textAlign: 'right' }}>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p, idx) => {
            const tone = STATUS_TONE[p.status] || 'secondary';
            const visibleAssets = (p.assets || []).slice(0, 6);
            const moreCount = (p.assets || []).length - visibleAssets.length;
            return (
              <React.Fragment key={p._id || idx}>
                <tr className={`kit-row tone-${tone}`}>
                  <td>
                    <div className="kit-id-cell">
                      <span className="kit-id-code">{p.friendly_id}</span>
                      <button
                        type="button"
                        className="kit-id-copy"
                        onClick={(e) => copyId(e, p.friendly_id)}
                        id={`tk-copy-${idx}`}
                        aria-label="Copy ID"
                      >
                        <i className="now-ui-icons files_single-copy-04" />
                      </button>
                      <UncontrolledTooltip target={`tk-copy-${idx}`}>{p.friendly_id}</UncontrolledTooltip>
                    </div>
                  </td>

                  {showPracticeColumn && (
                    <td>
                      {p.practice_data ? (
                        <div className="kit-practice-cell">
                          <div className="kit-practice-name">{p.practice_data.practice_name}</div>
                          {p.practice_data.owner && (
                            <div className="kit-practice-owner">
                              {p.practice_data.owner.firstName} {p.practice_data.owner.lastName}
                            </div>
                          )}
                        </div>
                      ) : <span className="kit-cell-muted">—</span>}
                    </td>
                  )}

                  <td><span className="kit-type-chip">{p.type || '—'}</span></td>

                  <td>
                    <div className="kit-status-cell">
                      <Badge color={tone} pill className="kit-status-badge">{t(p.status)}</Badge>
                      {p.status_date && (
                        <time className="kit-status-time">
                          {moment(p.status_date).format('DD-MM-YYYY HH:mm:ss')}
                        </time>
                      )}
                      {p.status === 'k_st_3' && p.practice_order_id && (
                        <a
                          href="#"
                          className="kit-status-link"
                          onClick={(e) => goToOrder(e, p.practice_order_id)}
                        >
                          {t('see-order')} →
                        </a>
                      )}
                    </div>
                  </td>

                  <td className="kit-actions-cell">
                    {!is_creating_kits && p.status !== 'k_st_7' && (
                      <div className="kit-actions">
                        <button
                          type="button"
                          className="kit-action-btn"
                          onClick={(e) => editPatientKit(e, p)}
                          id={`tk-view-${idx}`}
                        >
                          <i className="fa fa-eye" />
                        </button>
                        <UncontrolledTooltip target={`tk-view-${idx}`}>{t('view-btn') || 'View'}</UncontrolledTooltip>

                        {p.is_editable && (
                          <>
                            <button
                              type="button"
                              className="kit-action-btn"
                              onClick={(e) => editKit(e, p)}
                              id={`tk-edit-${idx}`}
                            >
                              <i className="fa fa-edit" />
                            </button>
                            <UncontrolledTooltip target={`tk-edit-${idx}`}>{t('edit-btn') || 'Edit'}</UncontrolledTooltip>
                          </>
                        )}

                        {p.status !== 'k_st_3' && (
                          <>
                            <button
                              type="button"
                              className="kit-action-btn kit-action-danger"
                              onClick={(e) => deleteKit(e, p)}
                              id={`tk-del-${idx}`}
                            >
                              <i className="fa fa-trash" />
                            </button>

                            {user.username === "kit-manager" && !['k_st_1', 'k_st_2'].includes(p.status) ? (<>
                              <Button
                                size="sm"
                                onClick={() => {
                                  clearKitAction(p._id);
                                }}
                              >
                                Clear kit
                              </Button>
                            </>) : (<></>)}

                            <UncontrolledTooltip target={`tk-del-${idx}`}>{t('delete-btn') || 'Delete'}</UncontrolledTooltip>
                          </>
                        )}

                        
                      </div>
                    )}
                  </td>
                </tr>
                <tr className="kit-row-assets">
                  <td colSpan={showPracticeColumn ? 5 : 4}>
                    <div className="kit-assets-row">
                      <span className="kit-assets-label">{t('assets')}</span>
                      {visibleAssets.length === 0 ? (
                        <span className="kit-cell-muted">{t('no-assets-short') || '—'}</span>
                      ) : (
                        visibleAssets.map((a) => (
                          <span className="kit-asset-chip" key={`a-${a._id || a}`}>
                            {a.asset_type ? `${a.asset_type}` : String(a)}
                            {a.brand && <small> · {a.brand}</small>}
                          </span>
                        ))
                      )}
                      {moreCount > 0 && (
                        <span className="kit-asset-more">+{moreCount}</span>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

TableKits.defaultProps = { data: [], loading: true };
TableKits.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export default translate('kits')(TableKits);
